import { defineMessages, type MessageDescriptor } from 'react-intl'

export interface PcmTestimonial {
  name: string
  title?: MessageDescriptor
  imageId: string
  review: MessageDescriptor
}

const pcmTestimonialMessages = defineMessages({
  partnershipsManagerTitle: {
    id: 'SFjamr0hU93fP9KlK8FO',
    defaultMessage: 'Partnerships Manager, Luxury Escapes',
    description: 'Job title for the travel expert shown alongside their offer page testimonial.',
  },
  strategicAccountDirectorTitle: {
    id: 'LkDA1UUSBblvlOE_Plnb',
    defaultMessage: 'Strategic Account Director & Mum of 2, Luxury Escapes',
    description: 'Job title for a travel expert shown alongside their offer page testimonial.',
  },
  merusakaNusaDuaReview: {
    id: '0_z4vj3eAekp19QQBYST',
    defaultMessage: 'One of the best all-inclusive resorts in Bali. I love the premium all-inclusive package, which includes premium drinks and unrestricted dining. The resort itself sits on my favourite beach in Nusa Dua – white sand, calm waters and perfect for swimming.',
    description: 'Travel expert testimonial quote for the Merusaka Nusa Dua offer page panel.',
  },
  iririkiIslandResortReview: {
    id: 'lua0fqVfh__zw1qlXG_m',
    defaultMessage: 'Most South Pacific island escapes require a bit of a commute from the “main island”. At Iririki, you’re on an island but you are literally 300 metres from the hustle and bustle of downtown Port Vila. The most memorable thing for me was the clarity of the water – you can snorkel right off the island, no need for boats or day trips.',
    description: 'Travel expert testimonial quote for the Iririki Island Resort offer page panel.',
  },
  ultiqaBurleighMediterraneanReview: {
    id: 'R09Jjg0pznaZFlsQYQai',
    defaultMessage: 'Stay where the Gold Coast locals hang out, surrounded by amazing beaches, cafes and restaurants. You’re at the southern end of the coast, which has such beauty. The apartments are spacious but if you can, upgrade to the ocean view rooms, they’re worth it.',
    description: 'Travel expert testimonial quote for the ULTIQA Burleigh Mediterranean Resort offer page panel.',
  },
  q1ResortAndSpaReview: {
    id: 'o2IK_LOv_EsLqWoXF7p9',
    defaultMessage: 'If you are after iconic, spacious apartments with ocean views in Surfers Paradise, this is the property to choose. Q1 is the tallest residential tower overlooking the beautiful Gold Coast. A must-see is a visit to the observation deck on top of the building – the sunset is magic.',
    description: 'Travel expert testimonial quote for the Q1 Resort and Spa offer page panel.',
  },
  mercureMaldivesKooddooReview: {
    id: 'dJTMuhhYWxAsOXot0DY1',
    defaultMessage: 'One of the best value all-inclusive properties we run and the house reef is amazing – you can snorkel straight from the Overwater Villas whenever you want, no boats or excursions required, which is pretty unique.',
    description: 'Travel expert testimonial quote for the Mercure Maldives Kooddoo offer page panel.',
  },
  pullmanMaldivesMaamutaaReview: {
    id: 'yiLCwvz6_SRF68ibZgnz',
    defaultMessage: 'At most Maldives properties, overwater costs a lot more than a beach villa, so the Ocean Villa is exceptional value for that classic overwater experience. Plus the variety of drinks is great, you can choose from over 80 brands, and the minibar gets restocked up every day too!',
    description: 'Travel expert testimonial quote for the Pullman Maldives Maamutaa offer page panel.',
  },
  meyyafushiMaldivesReview: {
    id: 'QmAK_Qpk3ymSXY9_UZTy',
    defaultMessage: 'It’s a brand new property so the villas are super modern, and all of them have pools with lots offering slides into the water too! The all-inclusive dining is premium as well, with quality food and drinks available 24 hours a day.',
    description: 'Travel expert testimonial quote for the Meyyafushi Maldives offer page panel.',
  },
  meliaHoTramReview: {
    id: 'VL9SmZEEc7ddAWILxvDB',
    defaultMessage: 'One of the best things is that once you arrive in HCMC, there’s no internal flight required, just a drive from the airport. The all-inclusive package is dine around across all restaurants so you could be there for over a week and still try new meals!',
    description: 'Travel expert testimonial quote for the Melia Ho Tram offer page panel.',
  },
  essencePeregianBeachReview: {
    id: '6YqIYeny8N-3xF-MwU6r',
    defaultMessage: 'Peregian Beach has it all. The beach is wider and quieter, and The Village is just a 3-minute walk from Essence across a low-traffic road. You’ll find gorgeous beachy boutiques, independent cafes, restaurants, happy hour spots, and personal services to keep you busy for hours.',
    description: 'Travel expert testimonial quote for the Essence Peregian Beach offer page panel.',
  },
  hyattRegencySamuiReview: {
    id: 'Y36HCp0TvvGpSzPKhMXQ',
    defaultMessage: 'The beachfront setting is what stays with you. The location feels peaceful and secluded, yet you’re still within easy reach of Chaweng and Choeng Mon. Dinner at the beachfront restaurant is a must for freshly caught seafood and sunset views. It works beautifully for families too, with movie nights, all-day ice cream, a playground and plenty of space for kids to explore.',
    description: 'Travel expert testimonial quote for the Hyatt Regency Samui offer page panel.',
  },
  panPacificMelbourneReview: {
    id: 'O2Qr7m9H-9BaToCIxRrb',
    defaultMessage: 'If you’re after floor-to-ceiling views of the Yarra River and fantastic five-star service, Pan Pacific Melbourne is the pick for you. If you want to make your stay even better, an upgrade to the Pacific Club King room is well worth it.',
    description: 'Travel expert testimonial quote for the Pan Pacific Melbourne offer page panel.',
  },
  panPacificPerthReview: {
    id: 'IHncan3dX4KHFfCF616p',
    defaultMessage: 'With the recent $50 million refurbishment, the entire hotel and all the rooms have been given a full facelift. The experience now feels truly five-star, and you can’t beat Pan Pacific’s location for a CBD stay within walking distance of the Swan River and Langley Park.',
    description: 'Travel expert testimonial quote for the Pan Pacific Perth offer page panel.',
  },
  theDarlingSydneyReview: {
    id: 'yDBpiQ0GTCJw69efvlyu',
    defaultMessage: 'Located in The Star Sydney precinct, you’re surrounded by award-winning restaurants, bars, retail and entertainment. Sokyo is perfect for world-class Japanese before a show, and if you really want to treat yourself, a treatment at The Darling Spa is a must.',
    description: 'Travel expert testimonial quote for The Darling Sydney offer page panel.',
  },
  outriggerMaldivesReview: {
    id: '47Cklw71G9V4jH4qUvxE',
    defaultMessage: 'Beautiful natural island with powdery white sand and one bluest lagoons I’ve seen in the Maldives. One of the only places you can swim with Whale Sharks and Manta Rays anytime of the year. They have a private island a few minutes by speedboat and you can book truly once-in-a-lifetime experiences like a beach BBQ with Champagne.',
    description: 'Travel expert testimonial quote for the Outrigger Maldives Maafushivaru offer page panel.',
  },
  dreamDowntownReview: {
    id: 'Hu6ihscoi3BmdHApvDfm',
    defaultMessage: 'Dream Downtown epitomises the laidback, cool vibes you’d expect from being in the heart of Manhattan. Once you have a cocktail in hand, you won’t really want to leave the beach club, despite having New York on your doorstep. The Chinese restaurant Philippe is not to be missed, but being so close to Chelsea Market, Meatpacking and the Piers gives you a wealth of New York’s finest food scene within walking distance.',
    description: 'Travel expert testimonial quote for the Dream Downtown offer page panel.',
  },
  wayfinderWaikikiReview: {
    id: 'aDKrIJw2Z2XANifsz94U',
    defaultMessage: 'Wayfinder Waikiki is where seasoned Hawaii travellers flock to for a truly Polynesian experience. Redfish restaurant is always one I seek out whenever I’m in Waikiki, offering some of the best poke on the island.',
    description: 'Travel expert testimonial quote for the Wayfinder Waikiki offer page panel.',
  },
  outriggerWaikikiBeachcomberReview: {
    id: '7mtj7wUanJn2fZalGdDL',
    defaultMessage: 'Outrigger Waikiki is a staple of Oahu. The activities across the resort allow you to immerse yourself in your stay, with complimentary lei making, live music and even sunrise yoga. If you love coffee, Hawaiian Aroma should be the number one stop for your daily fix, with the added bonus of fresh acai bowls, smoothies and easy access to the pool.',
    description: 'Travel expert testimonial quote for the Outrigger Waikiki Beachcomber Hotel offer page panel.',
  },
  hiltonGardenInnWaikikiBeachReview: {
    id: 'bnTzW_RCVDVlIRviRc3y',
    defaultMessage: 'Hilton Garden Inn Waikiki could not be more centrally located, putting you in one of the best spots to experience Waikiki on foot. The trouble is, being opposite International Market Place means you’ll likely spend plenty of time carrying shopping bags back and forth between the hotel and the shops! Not to worry though, the hotel is still a great spot for lazing by the rooftop pool.',
    description: 'Travel expert testimonial quote for the Hilton Garden Inn Waikiki Beach offer page panel.',
  },
  slsPlayaMujeresReview: {
    id: 'o4KeXa0HOqc-Wm3d8ukH',
    defaultMessage: 'Photos do not do this place justice. As soon as you arrive, you are transported to an oasis you won’t want to leave. I highly recommend spending an afternoon at Golden Hour, enjoying the drinks and food while you watch the sunset over the lagoon. There’s plenty of space across the resort, including a large adults-only pool, so it can be enjoyed by all without feeling crowded. Ciel Spa is perfect for unwinding, especially the hydrotherapy area, and with 17 restaurants and bars, there’s something for everyone.',
    description: 'Travel expert testimonial quote for the SLS Playa Mujeres offer page panel.',
  },
  qtGoldCoastReview: {
    id: '1WJlF7U1Dtm8OtcyJN2E',
    defaultMessage: 'QT Gold Coast delivers one of the Gold Coast’s standout buffet breakfasts. With traditional favourites alongside genuine wellness options, there’s something for everyone. The location is hard to beat. Expert tip: start your morning with a walk along the beach before heading back for breakfast. It sets the tone for the whole trip. Great pool, a good bar, and it works just as well for couples as it does for families.',
    description: 'Travel expert testimonial quote for the QT Gold Coast offer page panel.',
  },
  sofitelSaigonReview: {
    id: 'o-RoBN7WWDiWz_RjrMxY',
    defaultMessage: 'The Sofitel Saigon Plaza is everything you want from a Sofitel: impeccable fixtures and fittings, five-star service, and a distinctly Vietnamese soul that hits you the moment you walk into the lobby. The mix of French elegance and local character makes you feel immediately at home. The location in District 1 is ideal for first-timers, with the Notre Dame Cathedral, Central Post Office and some of the city’s best streets all within walking distance.',
    description: 'Travel expert testimonial quote for the Sofitel Saigon Plaza offer page panel.',
  },
  khatathaniReview: {
    id: 'sv73hQ-OAf2xdwhJ_E6W',
    defaultMessage: 'It doesn’t get better than spending your days in the sun by the huge pool areas with your family – floating on the lazy river, watching your kids go up and down waterslides and jumping through waves. When it’s time for a break there are plenty of dining options to refuel and do it all again. With everything you could want in one place, this resort is the perfect spot for a tropical family holiday.',
    description: 'Travel expert testimonial quote for the Khatathani offer page panel.',
  },
  ritzCarltonDubaiReview: {
    id: 'HZUEdoFkWVme3z8u-9H0',
    defaultMessage: 'The standout here is Club Lounge access: book a Club Room and you get complimentary free-flow premium beverages for up to ten hours a day, which is genuinely rare. It sits on one of the finest stretches of Jumeirah Beach, with views across The Palm and a private beach that feels a world away from the city.',
    description: 'Travel expert testimonial quote for the Ritz Carlton Dubai offer page panel.',
  },
  lotteAraiJapanReview: {
    id: 'Sk8S1gB8BoDOElQeaJ6d',
    defaultMessage: 'Lotte Arai gets some of the deepest powder in Japan, with well over 10 metres falling each season. When ski patrol opens the upper gates after a storm, you’re skiing waist-deep powder inside the resort boundary – basically something you can’t get anywhere else in Japan without hiring a backcountry guide.',
    description: 'Travel expert testimonial quote for the Lotte Arai Japan offer page panel.',
  },
  aulusLindosRhodesReview: {
    id: 'QJ9O1CCN84kPNkxJHG2a',
    defaultMessage: 'Aulūs Lindos Rhodes is in an amazing part of Rhodes, with serene beaches and incredible greenery. I’d recommend hiring a car for a day or two to explore Lindos – the whitewashed streets, rooftop restaurants, and views from the Acropolis are stunning – and some of the quieter beaches nearby. The resort itself is relaxed and spacious, ideal if you’re looking to switch off, and the all-inclusive offering is amazing. Also important to highlight: unlimited gelato throughout the day!',
    description: 'Travel expert testimonial quote for the Aulūs Lindos Rhodes offer page panel.',
  },
})

export const DEFAULT_PCM_TESTIMONIAL_TITLE = pcmTestimonialMessages.partnershipsManagerTitle

const MERUSAKA_NUSA_DUA_TESTIMONIAL: PcmTestimonial = {
  name: 'Aaron Corbett',
  imageId: 'aaron-corbett',
  review: pcmTestimonialMessages.merusakaNusaDuaReview,
}

const IRIRIKI_ISLAND_RESORT_TESTIMONIAL: PcmTestimonial = {
  name: 'David Callan',
  imageId: 'david-callan',
  review: pcmTestimonialMessages.iririkiIslandResortReview,
}

const ULTIQA_BURLEIGH_MEDITERRANEAN_RESORT_TESTIMONIAL: PcmTestimonial = {
  name: 'Emma Dixon',
  imageId: 'emma-dixon',
  review: pcmTestimonialMessages.ultiqaBurleighMediterraneanReview,
}

const Q1_RESORT_AND_SPA_TESTIMONIAL: PcmTestimonial = {
  name: 'Emma Dixon',
  imageId: 'emma-dixon',
  review: pcmTestimonialMessages.q1ResortAndSpaReview,
}

const MERCURE_MALDIVES_KOODDOO_TESTIMONIAL: PcmTestimonial = {
  name: 'Michael Thompson',
  imageId: 'michael-thompson',
  review: pcmTestimonialMessages.mercureMaldivesKooddooReview,
}

const PULLMAN_MALDIVES_MAAMUTAA_TESTIMONIAL: PcmTestimonial = {
  name: 'Michael Thompson',
  imageId: 'michael-thompson',
  review: pcmTestimonialMessages.pullmanMaldivesMaamutaaReview,
}

const MEYYAFUSHI_MALDIVES_TESTIMONIAL: PcmTestimonial = {
  name: 'Michael Thompson',
  imageId: 'michael-thompson',
  review: pcmTestimonialMessages.meyyafushiMaldivesReview,
}

const MELIA_HO_TRAM_TESTIMONIAL: PcmTestimonial = {
  name: 'Kane McKay',
  imageId: 'kane-mckay',
  review: pcmTestimonialMessages.meliaHoTramReview,
}

const ESSENCE_PEREGIAN_BEACH_TESTIMONIAL: PcmTestimonial = {
  name: 'Ann-Marie Kerry',
  imageId: 'ann-marie-kerry',
  review: pcmTestimonialMessages.essencePeregianBeachReview,
}

const HYATT_REGENCY_SAMUI_TESTIMONIAL: PcmTestimonial = {
  name: 'Thomas Yarwood',
  imageId: 'thomas-yarwood',
  review: pcmTestimonialMessages.hyattRegencySamuiReview,
}

const PAN_PACIFIC_MELBOURNE_TESTIMONIAL: PcmTestimonial = {
  name: 'Angus Crichton',
  imageId: 'angus-crichton',
  review: pcmTestimonialMessages.panPacificMelbourneReview,
}

const PAN_PACIFIC_PERTH_TESTIMONIAL: PcmTestimonial = {
  name: 'Angus Crichton',
  imageId: 'angus-crichton',
  review: pcmTestimonialMessages.panPacificPerthReview,
}

const THE_DARLING_SYDNEY_TESTIMONIAL: PcmTestimonial = {
  name: 'Angus Crichton',
  imageId: 'angus-crichton',
  review: pcmTestimonialMessages.theDarlingSydneyReview,
}

const OUTRIGGER_MALDIVES_TESTIMONIAL: PcmTestimonial = {
  name: 'Michael Thompson',
  imageId: 'michael-thompson',
  review: pcmTestimonialMessages.outriggerMaldivesReview,
}

const DREAM_DOWNTOWN_TESTIMONIAL: PcmTestimonial = {
  name: 'Dean McCullagh',
  imageId: 'dean-mccullagh',
  review: pcmTestimonialMessages.dreamDowntownReview,
}

const WAYFINDER_WAIKIKI_TESTIMONIAL: PcmTestimonial = {
  name: 'Dean McCullagh',
  imageId: 'dean-mccullagh',
  review: pcmTestimonialMessages.wayfinderWaikikiReview,
}

const OUTRIGGER_WAIKIKI_BEACHCOMBER_HOTEL_TESTIMONIAL: PcmTestimonial = {
  name: 'Dean McCullagh',
  imageId: 'dean-mccullagh',
  review: pcmTestimonialMessages.outriggerWaikikiBeachcomberReview,
}

const HILTON_GARDEN_INN_WAIKIKI_BEACH_TESTIMONIAL: PcmTestimonial = {
  name: 'Dean McCullagh',
  imageId: 'dean-mccullagh',
  review: pcmTestimonialMessages.hiltonGardenInnWaikikiBeachReview,
}

const SLS_PLAYA_MUJERES_TESTIMONIAL: PcmTestimonial = {
  name: 'Alexis Cunningham',
  imageId: 'alexis-cunningham',
  review: pcmTestimonialMessages.slsPlayaMujeresReview,
}

const QT_GOLD_COAST_TESTIMONIAL: PcmTestimonial = {
  name: 'Christie Fekete',
  title: pcmTestimonialMessages.strategicAccountDirectorTitle,
  imageId: 'christie-headshot',
  review: pcmTestimonialMessages.qtGoldCoastReview,
}

const SOFITEL_SAIGON_TESTIMONIAL: PcmTestimonial = {
  name: 'Christie Fekete',
  title: pcmTestimonialMessages.strategicAccountDirectorTitle,
  imageId: 'christie-headshot',
  review: pcmTestimonialMessages.sofitelSaigonReview,
}

const KHATATHANI_TESTIMONIAL: PcmTestimonial = {
  name: 'Ben McKeon',
  imageId: 'ben-mckeon',
  review: pcmTestimonialMessages.khatathaniReview,
}

const RITZ_CARLTON_DUBAI_TESTIMONIAL: PcmTestimonial = {
  name: 'Neekita Naidu',
  imageId: 'neekita-naidu',
  review: pcmTestimonialMessages.ritzCarltonDubaiReview,
}

const LOTTE_ARAI_JAPAN_TESTIMONIAL: PcmTestimonial = {
  name: 'Troy Rosee',
  imageId: 'troy-rosee',
  review: pcmTestimonialMessages.lotteAraiJapanReview,
}

const AULUS_LINDOS_RHODES_TESTIMONIAL: PcmTestimonial = {
  name: 'Elizabeth Tweedy',
  imageId: 'elizabeth-tweedy',
  review: pcmTestimonialMessages.aulusLindosRhodesReview,
}

export const PCM_TESTIMONIALS_BY_OFFER: Record<string, PcmTestimonial> = {
  '006RF00000Utey1YAB': MERUSAKA_NUSA_DUA_TESTIMONIAL,
  '006RF00000UpZVVYA3': IRIRIKI_ISLAND_RESORT_TESTIMONIAL,
  '006RF00000WVfxMYAT': ULTIQA_BURLEIGH_MEDITERRANEAN_RESORT_TESTIMONIAL,
  '006RF00000V09MVYAZ': Q1_RESORT_AND_SPA_TESTIMONIAL,
  '006RF00000XrYANYA3': MERCURE_MALDIVES_KOODDOO_TESTIMONIAL,
  '006RF00000XqymhYAB': MERCURE_MALDIVES_KOODDOO_TESTIMONIAL,
  '006RF00000Xqyl4YAB': PULLMAN_MALDIVES_MAAMUTAA_TESTIMONIAL,
  '006RF00000XqK7iYAF': PULLMAN_MALDIVES_MAAMUTAA_TESTIMONIAL,
  '006RF00000W8WoIYAV': MEYYAFUSHI_MALDIVES_TESTIMONIAL,
  '006RF00000XNHErYAP': MEYYAFUSHI_MALDIVES_TESTIMONIAL,
  '006RF00000VEXDbYAP': MELIA_HO_TRAM_TESTIMONIAL,
  '006RF00000ToeUbYAJ': ESSENCE_PEREGIAN_BEACH_TESTIMONIAL,
  '006RF00000TrcssYAB': ESSENCE_PEREGIAN_BEACH_TESTIMONIAL,
  '006RF00000U00I5YAJ': HYATT_REGENCY_SAMUI_TESTIMONIAL,
  '006RF00000UXG1XYAX': PAN_PACIFIC_MELBOURNE_TESTIMONIAL,
  '006RF00000W1QAzYAN': PAN_PACIFIC_PERTH_TESTIMONIAL,
  '006RF00000QLzmHYAT': THE_DARLING_SYDNEY_TESTIMONIAL,
  '006RF00000OXJu5YAH': OUTRIGGER_MALDIVES_TESTIMONIAL,
  '006RF00000TuTAEYA3': DREAM_DOWNTOWN_TESTIMONIAL,
  '006RF00000VPY35YAH': WAYFINDER_WAIKIKI_TESTIMONIAL,
  '006RF00000QBkv0YAD': OUTRIGGER_WAIKIKI_BEACHCOMBER_HOTEL_TESTIMONIAL,
  '006RF00000SsQv3YAF': HILTON_GARDEN_INN_WAIKIKI_BEACH_TESTIMONIAL,
  '006RF00000HfC3RYAV': SLS_PLAYA_MUJERES_TESTIMONIAL,
  '006RF00000Us9OPYAZ': QT_GOLD_COAST_TESTIMONIAL,
  '006RF00000UcxfKYAR': SOFITEL_SAIGON_TESTIMONIAL,
  '006RF00000VKOfSYAX': KHATATHANI_TESTIMONIAL,
  '006RF00000XYytBYAT': RITZ_CARLTON_DUBAI_TESTIMONIAL,
  '006RF00000UZBc1YAH': LOTTE_ARAI_JAPAN_TESTIMONIAL,
  '006RF00000SlDhfYAF': AULUS_LINDOS_RHODES_TESTIMONIAL,
}

export function getPcmTestimonialForOfferId(offerId: string): PcmTestimonial | undefined {
  return PCM_TESTIMONIALS_BY_OFFER[offerId]
}

export function hasPcmTestimonialForOfferId(offerId: string): boolean {
  return !!getPcmTestimonialForOfferId(offerId)
}
